// Librairies
import Cookies from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';
import { faDownload, faPen, faPlus, faTrash, faUpload, faUserEdit, faUserMinus, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { union, point, polygon, lineString, booleanIntersects, centroid, featureCollection } from '@turf/turf';
import i18n from '../locales/i18n';
// Services
import ProjectsService from '../services/ProjectsService';
import CustomFieldsService from '../services/CustomFieldsService';
// Utils
import TreesUtil from './TreesUtil';
import GreenSpacesUtil from './GreenSpacesUtil';
import FurnituresUtil from './FurnituresUtil';
import MarkersUtil from './MarkersUtil';
import FormattersUtil from './FormattersUtil';
import { showToast } from './ToastsUtil';
import WebSocketUtil from './WebSocketUtil';
import GeometriesUtil from './GeometriesUtil';
import RightsUtil from './RightsUtil';

export default class ProjectsUtil {
    static getRequiredFields() {
        return {
            trees: TreesUtil.getFields(false),
            greenSpaces: GreenSpacesUtil.getFields(),
            furnitures: FurnituresUtil.getFields(),
            markers: MarkersUtil.getFields()
        }
    }

    static getProjectRequiredFields(project, projectId, organization) {
        const projectSubscription = project.organization?.subscription || organization;
        let requiredFields = this.getRequiredFields();

        if (!project) return requiredFields;
        else if (project.requiredFields) {
            let projectRequiredFields = {};
            if (Array.isArray(project.requiredFields)) {
                if (projectId) projectRequiredFields = JSON.parse(project.requiredFields.find(rf => JSON.parse(rf).projectId === projectId));
                else project.requiredFields.forEach(rf => projectRequiredFields = { ...projectRequiredFields, ...JSON.parse(rf) });
            } else if (project.requiredFields.constructor === ({}).constructor) projectRequiredFields = project.requiredFields;
            else projectRequiredFields = JSON.parse(project.requiredFields);

            let rf = {
                trees: { ...requiredFields.trees, ...projectRequiredFields?.trees },
                greenSpaces: { ...requiredFields.greenSpaces, ...projectRequiredFields?.greenSpaces },
                furnitures: { ...requiredFields.furnitures, ...projectRequiredFields?.furnitures },
                markers: { ...requiredFields.markers, ...projectRequiredFields?.markers }
            };

            if (!projectSubscription?.expertMode)
                ['root', 'collar', 'trunk', 'branch', 'leaf'].forEach(organ => {
                    ['Symptoms', 'Pathogens', 'Pests', 'Epiphytes'].forEach(property => {
                        rf.trees[`${organ}${property}`] = false;
                    });
                });

            if (project.type === 'project') project.requiredFields = JSON.stringify(rf);
            return rf;
        } else { // Si le projet n'a pas de requiredFields, on les ajoute
            project.requiredFields = JSON.stringify(requiredFields);
            return requiredFields;
        }
    }

    static getPublicFields() {
        return {
            trees: TreesUtil.getFields(true),
            greenSpaces: GreenSpacesUtil.getFields(true),
            furnitures: FurnituresUtil.getFields(true),
            markers: MarkersUtil.getFields(true),
            main: {
                images: true,
                trees: true,
                greenSpaces: true,
                furnitures: true,
                markers: true,
                stations: true,
                references: true,
                actions: true,
                events: true,
                photos: true,
                files: true,
                filters: true,
                statistics: true,
                charts: true,
                dataTable: true,
                thematicMaps: true,
                wmsServices: true
            }
        }
    }

    static getProjectPublicFields(project, projectCollaborators = []) {
        projectCollaborators = projectCollaborators || [];
        const isUserInProject = projectCollaborators.length ? this.isUserInProject(projectCollaborators) : false;
        let publicFields = this.getPublicFields();

        if (!project) return publicFields;
        else if (project.publicFields) {
            let projectPublicFields = {};
            if (project.publicFields.constructor === ({}).constructor) projectPublicFields = project.publicFields;
            else projectPublicFields = JSON.parse(project.publicFields);

            let pf = {
                main: { ...publicFields.main, ...projectPublicFields?.main },
                trees: { ...publicFields.trees, ...projectPublicFields?.trees },
                greenSpaces: { ...publicFields.greenSpaces, ...projectPublicFields?.greenSpaces },
                furnitures: { ...publicFields.furnitures, ...projectPublicFields?.furnitures },
                markers: { ...publicFields.markers, ...projectPublicFields?.markers }
            };

            project.publicFields = JSON.stringify(pf);
            if (isUserInProject) {
                const userId = jwtDecode(new Cookies().get('token')).id;
                const projectCollaborator = projectCollaborators.find(pc => pc.userId === userId);
                publicFields.main.trees = RightsUtil.canRead(projectCollaborator.projectRole.trees);
                publicFields.main.greenSpaces = RightsUtil.canRead(projectCollaborator.projectRole.greenSpaces);
                publicFields.main.furnitures = RightsUtil.canRead(projectCollaborator.projectRole.furnitures);
                publicFields.main.markers = RightsUtil.canRead(projectCollaborator.projectRole.markers);
                publicFields.main.stations = RightsUtil.canRead(projectCollaborator.projectRole.stations);
                publicFields.main.actions = RightsUtil.canRead(projectCollaborator.projectRole.actions);
                publicFields.main.events = RightsUtil.canRead(projectCollaborator.projectRole.events);
                publicFields.main.charts = RightsUtil.canRead(projectCollaborator.projectRole.charts);
                publicFields.main.dataTable = RightsUtil.canRead(projectCollaborator.projectRole.tables);
                publicFields.main.filters = RightsUtil.canRead(projectCollaborator.projectRole.filters);
                publicFields.main.statistics = RightsUtil.canRead(projectCollaborator.projectRole.statistics);
                publicFields.main.thematicMaps = RightsUtil.canRead(projectCollaborator.projectRole.thematicMaps);
                return publicFields;
            }

            return pf;
        } else { // Si le projet n'a pas de publicFields, on les ajoute
            project.publicFields = JSON.stringify(publicFields);
            return publicFields;
        }
    }

    static isUserInProject(userBaseProjects) {
        // Carte publique
        if (!userBaseProjects) return true;
        // Utilisateur non connecté accèdant au projet
        const cookie = new Cookies().get('token');
        if (!cookie) return false;
        const userId = jwtDecode(cookie).id;
        if (!userId) return false;
        // Utilisateur connecté accèdant au projet
        const userBaseProject = userBaseProjects.find(x => x.userId === userId);
        return userBaseProject ? true : false;
    }

    static assignProjectFormulaVersionsType(project, formulas) {
        if (project?.projectFormulaVersions) {
            project.projectFormulaVersions.forEach(pfv => {
                const formula = formulas.find(formula => (
                    formula.formulaVersions.find(formulaVersion => formulaVersion.id === pfv.formulaVersionId)
                ));
                pfv.formulaId = formula?.id;
                if (pfv.formulaId === 4) {
                    const formulaVersion = formula.formulaVersions.find(formulaVersion => formulaVersion.id === pfv.formulaVersionId);
                    pfv.formulaType = formulaVersion.label.toLowerCase().includes('wallonie') ? 'Wallonie'
                        : formulaVersion.label.toLowerCase().includes('bruxelles') ? 'Bruxelles'
                            : 'France';
                }
            });
        }
    }

    static getProjectLogTypes() {
        return {
            import: {
                label: i18n.t("Import"),
                icon: faUpload
            },
            export: {
                label: i18n.t("Export"),
                icon: faDownload
            },
            element_addition: {
                label: i18n.t("Ajout d'éléments"),
                icon: faPlus
            },
            element_update: {
                label: i18n.t("Mise à jour d'éléments"),
                icon: faPen
            },
            element_deletion: {
                label: i18n.t("Suppression d'éléments"),
                icon: faTrash
            },
            collaborator_addition: {
                label: i18n.t("Ajout de collaborateurs"),
                icon: faUserPlus
            },
            collaborator_update: {
                label: i18n.t("Mise à jour de collaborateurs"),
                icon: faUserEdit
            },
            collaborator_deletion: {
                label: i18n.t("Suppression de collaborateurs"),
                icon: faUserMinus
            }
        };
    }

    static isElementLocked(lockedElements, feature, project, projectCollaborators = [], users) {
        const userId = jwtDecode(new Cookies().get('token')).id;
        const lockedElement = lockedElements.find(le => le.id === feature.id);
        const isElementLocked = (lockedElement && lockedElement.userId !== userId) ? true : false;
        if (isElementLocked && project && Array.isArray(users)) {
            const user = projectCollaborators.find(up => up.user.id === lockedElement.userId)?.user;
            const formattedUser = FormattersUtil.formatLastNameAndFirstName(user.lastName, user.firstName);
            if (!users.includes(formattedUser)) {
                users.push(formattedUser);
                showToast('elements_locked', formattedUser);
            }
        }
        return isElementLocked;
    }

    static removeBaseProject(baseProjectId, baseProjects, rootBaseProjectIds, setProjects, setRootBaseProjectIds) {
        if (!baseProjectId || !baseProjects?.length) return;

        const baseProject = baseProjects.find(bp => bp.id === baseProjectId);
        if (!baseProject) return baseProjects;

        if (rootBaseProjectIds.length) setRootBaseProjectIds(rootBaseProjectIds.filter(id => id !== baseProjectId));
        baseProjects = baseProjects.filter(bp => bp.id !== baseProjectId && !bp.path?.includes(baseProjectId));
        setProjects(baseProjects);

        return baseProjects;
    }

    static getBaseProject(baseProjectId, baseProjects) {
        if (!baseProjectId || !baseProjects?.length) return;
        return baseProjects.find(bp => bp.id === baseProjectId);
    }

    static getAuthorizedSelectedBaseProjectIds(baseProjectsToMove, projects) {
        if (!baseProjectsToMove.length) return [];
        const userId = jwtDecode(new Cookies().get('token')).id;

        return baseProjectsToMove.filter(baseProject => // Vérification des droits
            projects.find(projectInProps => ((!baseProject.path && baseProject.id === projectInProps.id) || (baseProject.path && baseProject.path.includes(projectInProps.path || `/${projectInProps.id}/`))) &&
                projectInProps.userBaseProjects.find(ubp => ubp.userId === userId && ['owner', 'manager'].includes(ubp.projectRole.type)))).map(baseProject => baseProject.id);
    }

    // TODO Vérifier s'il faut adapter à la nouvelle liste de projets
    static checkIfCanMoveTo(destination = { id: null, path: null }, userId, baseProjectsToMove, baseProjects) {
        if (!baseProjectsToMove?.length || destination.type === 'project') return false;

        let canMoveTo = true;
        let ownerChanged = false;
        const destinationUserBaseProjects = !destination.id ? [] : baseProjects.find(projectInProps => ((!destination.path && (destination.id === projectInProps.id)) || (destination.path && destination.path.includes(projectInProps.path || `/${projectInProps.id}/`))))?.userBaseProjects;
        const destinationOwner = destinationUserBaseProjects.find(dubp => dubp.projectRole.type === 'owner');
        const destinationUser = destinationUserBaseProjects.find(dubp => dubp.userId === userId);
        for (const baseProjectToMove of baseProjectsToMove) {
            if (!baseProjectToMove.path && !destination.id) { // Racine => Racine
                canMoveTo = false;
                break;
            }

            if (destination.id && baseProjectToMove.type === 'folder' && // Destination => Parent actuel ou Dossier actuel ou Dossier enfant d'un dossier déplacé
                (destination.path?.includes(baseProjectToMove.id) || destination.id === baseProjectToMove.id || destination.id === baseProjectToMove.parentdestinationId)) {
                canMoveTo = false;
                break;
            } else if (destination.id && baseProjectToMove.path) { // Destination => Parent actuel
                const splitPath = baseProjectToMove.path.split('/').filter(id => id.length);
                if (Number(splitPath[splitPath.length - 1]) === destination.id) {
                    canMoveTo = false;
                    break;
                }
            }

            if (canMoveTo) { // Vérification des droits
                const rootFolderId = baseProjectToMove.path ? Number(baseProjectToMove.path.split('/')[1]) : null;
                const movedUserBaseProjects = rootFolderId
                    ? baseProjects.find(projectInProps => projectInProps.id === rootFolderId)?.userBaseProjects
                    : baseProjects.find(projectInProps => ((!baseProjectToMove.path && (baseProjectToMove.id === projectInProps.id)) || (baseProjectToMove.path && baseProjectToMove.path.includes(projectInProps.path || `/${projectInProps.id}/`))))?.userBaseProjects;
                const movedOwner = movedUserBaseProjects.find(ubp => ubp.projectRole.type === 'owner');
                if (destinationOwner && destinationOwner.userId !== movedOwner.userId) ownerChanged = true;
                if (destinationOwner && destinationUser && movedOwner.userId !== destinationOwner.userId && (movedOwner.userId !== userId || !['owner', 'manager'].includes(destinationUser.projectRole.type))) {
                    canMoveTo = false;
                    break;
                }
            }
        }

        return { isAllowed: canMoveTo, ownerChanged };
    }

    // TODO Mettre à jour la liste des UserBaseProjects des BaseProjects
    static moveBaseProjects(desinationFolder, authorizedBaseProjectIds, baseProjectsToMove, baseProjects, currentFolder, rootFolder, rootBaseProjectIds, setRootBaseProjectIds, webSocketHubs) {
        const folderId = desinationFolder?.id || 0;
        const folderPath = desinationFolder?.path;
        const newOrganizationId = desinationFolder?.organizationId;
        return new Promise((resolve, reject) => {
            baseProjectsToMove = JSON.parse(JSON.stringify(baseProjectsToMove.filter(bptm => authorizedBaseProjectIds.includes(bptm.id))));
            ProjectsService.moveBaseProjects(authorizedBaseProjectIds, folderId, baseProjectsToMove[0].type).then((response) => {
                if (response) {
                    const userBaseProjectsAdded = response.userProjectsAdded || [];
                    const userBaseProjectsRemoved = response.userProjectsRemoved || [];
                    let usersToAlert = []; // Récupérer les users depuis le root
                    baseProjects = JSON.parse(JSON.stringify(baseProjects));
                    baseProjectsToMove = baseProjects.filter(bp => baseProjectsToMove.find(bptm => bptm.id === bp.id));

                    // Mise à jour des parentFolderIds, des paths et des userBaseProjects
                    this.updatePath(baseProjects, baseProjectsToMove, folderId || null, folderId ? `${folderPath || '/'}${folderId}/` : null, userBaseProjectsAdded, userBaseProjectsRemoved);

                    // Mise à jour de l'organisation
                    if (newOrganizationId) {
                        const baseProjectIds = baseProjectsToMove.map(bp => bp.id);
                        const subBaseProjects = baseProjects.filter(bp => bp.path && baseProjectIds.some(id => bp.path.includes(id)));

                        [...baseProjectsToMove, ...subBaseProjects].forEach(baseProject => {
                            baseProject.organizationId = newOrganizationId;
                            if (desinationFolder?.organization)
                                baseProject.organization = desinationFolder.organization;
                        });
                    }

                    // Ajout des dossiers/projets dans le dossier de destination
                    if (!folderId) setRootBaseProjectIds([...rootBaseProjectIds, ...baseProjectsToMove.map(bp => bp.id)]);
                    else setRootBaseProjectIds(rootBaseProjectIds.filter(id => !baseProjectsToMove.find(bp => bp.id === id)));

                    const originalPath = baseProjectsToMove[0].path;
                    const currentProjectList = baseProjects;
                    rootFolder = rootFolder ? baseProjects.find(project => project.id === rootFolder.id) : null;
                    const userId = jwtDecode(new Cookies().get('token')).id;
                    usersToAlert = [...new Set(usersToAlert.filter(uta => uta.userId !== userId).map(uta => uta.userId))];
                    WebSocketUtil.moveProjects(webSocketHubs, usersToAlert, baseProjectsToMove, originalPath);
                    resolve({ currentProjectList, currentFolder, rootFolder, baseProjects });
                }
                else reject();
            });
        });
    }

    static updatePath(baseProjects, baseProjectsToMove, parentFolderId, path, userBaseProjectsAdded, userBaseProjectsRemoved) {
        baseProjectsToMove.forEach(baseProject => {
            baseProject.parentFolderId = parentFolderId;
            baseProject.path = path;
            baseProject.userBaseProjects = [...(baseProject.userBaseProjects || []), ...(userBaseProjectsAdded || []).filter(ubp => ubp.baseProjectId === baseProject.id)];
            if (userBaseProjectsRemoved?.length)
                baseProject.userBaseProjects = baseProject.userBaseProjects.filter(ubp => !userBaseProjectsRemoved.find(ubpr => ubpr.baseProjectId === baseProject.id && ubpr.userId === ubp.userId));

            // Mise à jour récursive
            const newPath = `${path || '/'}${baseProject.id}/`;
            const subProjects = baseProjects.filter(bp => bp.type === 'project' && bp.parentFolderId === baseProject.id);
            const subFolders = baseProjects.filter(bp => bp.type === 'folder' && bp.parentFolderId === baseProject.id);
            if (subProjects?.length)
                subProjects.forEach(project => {
                    project.path = newPath;
                    project.userBaseProjects = [...(project.userBaseProjects || []), ...(userBaseProjectsAdded || []).filter(ubp => ubp.baseProjectId === project.id)];
                    if (userBaseProjectsRemoved?.length)
                        project.userBaseProjects = project.userBaseProjects.filter(ubp => !userBaseProjectsRemoved.find(ubpr => ubpr.baseProjectId === project.id && ubpr.userId === ubp.userId));
                });
            if (subFolders) this.updatePath(baseProjects, subFolders, baseProject.id, newPath);
        });
    }

    static updateProjectsInProps(project, projects, formulas, projectInProps, setProjects, setProject) {
        if (projects) {
            projects = [...projects];
            this.assignProjectFormulaVersionsType(project, formulas);
            const index = projects.findIndex(p => p.id === project.id);
            if (index !== -1) projects[index] = { ...project };
            setProjects(JSON.parse(JSON.stringify(projects)));
            if (index !== -1 && projectInProps?.id === project.id) setProject({ ...project });
        } else if (projectInProps?.id === project.id) setProject({ ...project });
    }

    static mergeProjectsProperties(properties, removePlanifiedProjects = false) {
        if (removePlanifiedProjects) properties.baseProjects = properties.baseProjects.filter(bp => bp.type === 'folder' || bp.status !== 3);
        properties.baseProjects
            .filter(baseProject => baseProject.type === 'project')
            .forEach(baseProject => {
                const surroundings = GeometriesUtil.truncateCoordinates(JSON.parse(baseProject.surroundings), 10); // Pour éviter un crash de TurfJS quand énormément de coordonnées
                if (properties.bounds) properties.bounds = union(featureCollection([properties.bounds, surroundings]));
                else properties.bounds = surroundings;

                properties.projectLabels.push({
                    id: baseProject.id,
                    label: baseProject.label,
                    surroundings,
                    coordinates: centroid(surroundings).geometry.coordinates.toReversed()
                });

                properties.tags = [...properties.tags, ...(baseProject.tags || [])];
                properties.projectFormulaVersions = [...properties.projectFormulaVersions, ...(baseProject.projectFormulaVersions || [])];
                const requiredFields = baseProject.requiredFields ? [JSON.stringify({ ...JSON.parse(baseProject.requiredFields), projectId: baseProject.id })] : null;
                properties.requiredFields = [...properties.requiredFields, ...(requiredFields || [])];
            });
    }

    static getStatus() {
        return [
            { value: 1, text: i18n.t("Situation existante") },
            { value: 2, text: i18n.t("Situation existante - En cours d'inventaire") },
            { value: 3, text: i18n.t("Situation planifiée/projetée") }
        ];
    }

    static checkIfInsideSurroundings(shape, layer, surroundingsLayer, surroundings = null) {
        const surroundingsGeometry = surroundings || polygon(GeometriesUtil.convertPolygonLatLngsToCoordinates(surroundingsLayer.getLatLngs()));
        let geometryToCheck;
        switch (shape) {
            case 'marker': case 'point': geometryToCheck = point([layer.getLatLng().lng, layer.getLatLng().lat]); break;
            case 'line': geometryToCheck = lineString(GeometriesUtil.convertLineLatLngsToCoordinates(layer.getLatLngs())); break;
            case 'polygon': case 'rectangle': case 'circle':
                const newLayer = shape !== 'circle' ? layer : GeometriesUtil.convertCircleToPolygon(layer, null, 30);
                geometryToCheck = polygon(GeometriesUtil.convertPolygonLatLngsToCoordinates(newLayer.getLatLngs()));
                break;
            default: break;
        }

        const isIntersecting = surroundingsGeometry && geometryToCheck ? booleanIntersects(surroundingsGeometry, geometryToCheck) : false;
        return geometryToCheck && (surroundings ? isIntersecting : !isIntersecting);
    }

    static async loadMissingEntities(project, projectInProps, projectsInProps, formulas, setProject, setProjects, {
        missingEntities = null, setProjectCustomFields = null, setCustomFieldCategories = null
    } = {}) {
        if (!project?.id) return null;
        if (!missingEntities) {
            missingEntities = [];
            if (project.type === 'project' && !project.surroundings) missingEntities.push('surroundings');
            if (project.type === 'project' && !project.invitations) missingEntities.push('projectInvitations');
            if (project.type === 'project' && !project.organization?.organizationAccessRequests) missingEntities.push('organizationAccessRequests');
            if (project.type === 'project' && !project.organization?.organizationInvitations) missingEntities.push('organizationInvitations');
            if (project.type === 'project' && !project.projectFormulaVersions) missingEntities.push('projectFormulaVersions');
            if (project.type === 'project' && project.themeId && !project.theme) missingEntities.push('theme');
            if (project.type === 'project' && project.priceListId && !project.priceList) missingEntities.push('priceList');
            if (project.type === 'project' && !project.fieldCategories) missingEntities.push('fieldCategories');
            if (project.type === 'project' && !project.wmsServices) missingEntities.push('wmsServices');
            if (project.type === 'project' && !project.thematicMaps) missingEntities.push('thematicMaps');
            if (project.type === 'project' && !project.stationLevels) missingEntities.push('stationLevels');
            if (project.type === 'project' && !project.projectCustomFields) missingEntities.push('projectCustomFields');
            if (project.type === 'project' && !project.organizationCustomFieldCategories) missingEntities.push('organizationCustomFieldCategories');
            if (!project.projectRoleStations) missingEntities.push('projectRoleStations');
            if (!project.tags) missingEntities.push('tags');
        } else {
            missingEntities = missingEntities.filter(entity => {
                if (entity === 'surroundings') return !project.surroundings;
                if (entity === 'projectInvitations') return !project.invitations;
                if (entity === 'organizationInvitations') return !project.organization?.invitations;
                if (entity === 'organizationAccessRequests') return !project.organization?.organizationAccessRequests;
                if (entity === 'projectFormulaVersions') return !project.projectFormulaVersions;
                if (entity === 'theme') return project.themeId && !project.theme;
                if (entity === 'priceList') return project.priceListId && !project.priceList;
                if (entity === 'fieldCategories') return !project.fieldCategories;
                if (entity === 'wmsServices') return !project.wmsServices;
                if (entity === 'thematicMaps') return !project.thematicMaps;
                if (entity === 'stationLevels') return !project.stationLevels;
                if (entity === 'projectCustomFields') return !project.projectCustomFields;
                if (entity === 'organizationCustomFieldCategories') return !project.organizationCustomFieldCategories;
                if (entity === 'projectRoleStations') return !project.projectRoleStations;
                if (entity === 'tags') return !project.tags;
                return true;
            });
        }

        if (missingEntities.length > 0) {
            if (setProjectCustomFields && missingEntities.includes('projectCustomFields')) {
                const projectCustomFields = await CustomFieldsService.getProjectCustomFields(project.id);
                if (projectCustomFields) await setProjectCustomFields(project.id, projectCustomFields);
            }

            if (setCustomFieldCategories && missingEntities.includes('organizationCustomFieldCategories')) {
                const response = await CustomFieldsService.getCustomFieldCategories(project.organizationId);
                if (response) {
                    const { organizationId, customFieldCategories } = response;
                    await setCustomFieldCategories(organizationId, customFieldCategories);
                }
                missingEntities = missingEntities.filter(entity => entity !== 'organizationCustomFieldCategories');
            }

            const response = await ProjectsService.getBaseProjectSubEntities(project.id, missingEntities);
            if (response) {
                if (response.surroundings) project.surroundings = response.surroundings;
                if (response.projectInvitations) project.invitations = response.projectInvitations;
                if (response.organizationInvitations && project.organization) project.organization.invitations = response.organizationInvitations;
                if (response.organizationAccessRequests && project.organization) project.organization.organizationAccessRequests = response.organizationAccessRequests;
                if (response.projectFormulaVersions) project.projectFormulaVersions = response.projectFormulaVersions;
                if (response.theme) project.theme = response.theme;
                if (response.priceList) project.priceList = response.priceList;
                if (response.fieldCategories) project.fieldCategories = response.fieldCategories;
                if (response.projectCustomFields) project.projectCustomFields = response.projectCustomFields;
                if (response.wmsServices) project.wmsServices = response.wmsServices;
                if (response.thematicMaps) project.thematicMaps = response.thematicMaps;
                if (response.stationLevels) project.stationLevels = response.stationLevels;
                if (response.tags) project.tags = response.tags;
                if (response.projectRoleStations) {
                    for (let i = 0; i < (project.projectRoles?.length || 0); i++)
                        project.projectRoles[i].projectRoleStations = response.projectRoleStations.filter(prs => prs.projectRoleId === project.projectRoles[i].id);
                    for (let i = 0; i < (project.userBaseProjects?.length || 0); i++)
                        if (project.userBaseProjects[i].projectRole)
                            project.userBaseProjects[i].projectRole.projectRoleStations = response.projectRoleStations.filter(prs => prs.projectRoleId === project.userBaseProjects[i].projectRole.id);
                }
                ProjectsUtil.updateProjectsInProps(project, projectsInProps, formulas, projectInProps, setProjects, setProject);
            }

            // TODO Vérifier tout avec le compte Dev
            return response;
        }

        return null;
    }

    static async showFolderOnMap({
        folder,
        actions,
        events,
        setRights,
        setProjectCollaborators,
        shouldLoadSurroundings = true, // Optionnel
        projects, // Optionnel
        setProjects, // Optionnel
    }) {
        try {
            const response = await ProjectsService.getFolderElements(folder.id, shouldLoadSurroundings);

            // Attributions des actions et événements
            response.projectActions.forEach(pa => pa.action = actions.find(a => a.id === pa.actionId));
            response.projectEvents.forEach(pe => pe.event = events.find(e => e.id === pe.eventId));

            // Mise à jour des surroundings dans les projets
            if (projects?.length && setProjects && response?.projectSurroundings) {
                let updated = false;
                for (const [projectId, surroundings] of response.projects) {
                    const project = projects.find(p => p.id === Number(projectId));
                    if (project) {
                        project.surroundings = surroundings;
                        updated = true;
                    }
                }
                if (updated) setProjects(projects);
                delete response.projectSurroundings;
            }

            // Construction des propriétés des projets
            let properties = {
                baseProjects: response.projects || [],
                bounds: null,
                tags: [],
                projectFormulaVersions: [],
                requiredFields: [],
                projectLabels: [],
            };
            ProjectsUtil.mergeProjectsProperties(properties, true);

            delete response.projects;
            // Si pas de bounds → erreur
            if (!properties.bounds) showToast('empty_folder_viewing_not_allowed');
            else {
                const userBaseProject = (response.projectCollaborators || []).find(x => x.user.id === jwtDecode(new Cookies().get('token')).id);
                let rights = RightsUtil.getUserProjectRights(userBaseProject);
                rights = RightsUtil.setRightsReadOnly(rights);
                if (setRights) setRights(rights);

                const projectCollaborators = response.projectCollaborators;
                delete response.projectCollaborators;
                if (setProjectCollaborators) setProjectCollaborators(projectCollaborators);

                return {
                    rights,
                    projectCollaborators,
                    folder: {
                        ...folder,
                        ...response,
                        surroundings: JSON.stringify(properties.bounds),
                        projectFormulaVersions: properties.projectFormulaVersions,
                        requiredFields: properties.requiredFields,
                        projectLabels: properties.projectLabels
                    }
                };
            }

            return null;
        } catch (error) {
            console.error("Erreur lors de l'ouverture du dossier :", error);
            return null;
        }
    }

    static getDefaultBaseProjectDataLoadingStatus() {
        return {
            treesStatus: 0,
            greenSpacesStatus: 0,
            furnituresStatus: 0,
            markersStatus: 0,
            stationsStatus: 0,
            backgroundImagesStatus: 0,
            actionsStatus: 0,
            eventsStatus: 0,
            photosStatus: 0,
            filesStatus: 0,
            linkedElementsStatus: 0,
            stationElementsStatus: 0,
            customFieldsStatus: 0,
        };
    }
}